import React, { useState, useEffect, useMemo } from 'react';
import { ARTICLES as FALLBACK_ARTICLES } from '../data/articles';
import { ArrowRight, ArrowLeft, Clock, Calendar, Search, X, TrendingUp, Mail, Tag as TagIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import NewsletterSignup from './NewsletterSignup';

const InsightsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState(FALLBACK_ARTICLES);
  const [popular, setPopular] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeTag, setActiveTag] = useState(searchParams.get('tag') || '');
  const [query, setQuery] = useState('');

  useEffect(() => {
    api.getArticles().then(data => {
      if (Array.isArray(data) && data.length > 0) setArticles(data);
    }).catch(() => {});
    api.getPopularArticles(4).then(data => {
      if (Array.isArray(data)) setPopular(data);
    }).catch(() => {});
    api.getArticleTags().then(data => {
      if (Array.isArray(data)) setTagOptions(data);
    }).catch(() => {});
  }, []);

  // Sync tag filter to URL
  useEffect(() => {
    if (activeTag) {
      setSearchParams({ tag: activeTag }, { replace: true });
    } else if (searchParams.get('tag')) {
      const next = new URLSearchParams(searchParams);
      next.delete('tag');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTag]);

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(articles.map(a => a.category)))],
    [articles]
  );

  const filtered = useMemo(() => {
    let list = activeCategory === 'All' ? articles : articles.filter(a => a.category === activeCategory);
    if (activeTag) {
      list = list.filter(a => Array.isArray(a.tags) && a.tags.includes(activeTag));
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(a =>
        a.title.toLowerCase().includes(q) ||
        a.excerpt.toLowerCase().includes(q) ||
        (a.body || '').toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q) ||
        (Array.isArray(a.tags) ? a.tags.some(t => t.toLowerCase().includes(q)) : false)
      );
    }
    return list;
  }, [articles, activeCategory, activeTag, query]);

  const showFeatured = activeCategory === 'All' && !activeTag && !query.trim();

  const clearAll = () => {
    setQuery('');
    setActiveCategory('All');
    setActiveTag('');
  };

  return (
    <div className="bg-[#FDFEFA] min-h-screen" data-testid="insights-page">
      {/* Header */}
      <div className="bg-[#242326] pt-24 pb-16 md:pb-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <button
            onClick={() => navigate('/')}
            data-testid="insights-back-home"
            className="inline-flex items-center gap-2 text-white/50 text-sm font-['Inter'] mb-8 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white text-4xl md:text-6xl lg:text-[72px] font-light leading-[1.1] tracking-tight font-['Inter'] mb-4"
          >
            Insights
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-white/60 text-lg md:text-xl font-['Lato'] font-light max-w-2xl"
          >
            Thinking on growth marketing, adtech, and brand strategy from the team that runs the campaigns.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-6 flex flex-wrap items-center gap-3"
          >
            <button
              onClick={() => navigate('/newsletter')}
              data-testid="insights-header-archive-link"
              className="text-white/60 text-xs font-['Inter'] uppercase tracking-wider hover:text-[#83BF42] transition-colors"
            >
              Newsletter archive →
            </button>
            <span className="text-white/20 text-xs">·</span>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/rss.xml`}
              target="_blank"
              rel="noreferrer"
              data-testid="insights-header-rss-link"
              className="text-white/60 text-xs font-['Inter'] uppercase tracking-wider hover:text-[#83BF42] transition-colors"
            >
              RSS feed →
            </a>
          </motion.div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`insights-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-['Inter'] transition-all duration-300 ${
                  activeCategory === cat
                    ? 'bg-[#242326] text-white'
                    : 'bg-[#242326]/5 text-[#4A4A48] hover:bg-[#242326]/10'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-80">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#B8B8B8]" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles…"
              data-testid="insights-search-input"
              className="w-full pl-11 pr-10 py-2.5 rounded-full bg-[#242326]/5 text-[#242326] placeholder-[#B8B8B8] font-['Lato'] text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#83BF42]/30 transition-all"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                data-testid="insights-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B8B8B8] hover:text-[#242326]"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Tag row */}
        {tagOptions.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="insights-tags-row">
            <span className="text-[#B8B8B8] text-[11px] uppercase tracking-wider font-['Inter'] flex items-center gap-1 mr-1">
              <TagIcon className="w-3 h-3" /> Tags
            </span>
            {tagOptions.slice(0, 12).map(({ tag, count }) => (
              <button
                key={tag}
                onClick={() => setActiveTag(activeTag === tag ? '' : tag)}
                data-testid={`insights-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-1 rounded-full text-xs font-['Inter'] transition-colors ${
                  activeTag === tag
                    ? 'bg-[#83BF42] text-white'
                    : 'bg-transparent text-[#4A4A48] border border-[#242326]/10 hover:border-[#83BF42]/40 hover:text-[#83BF42]'
                }`}
              >
                #{tag}
                <span className={`ml-1 ${activeTag === tag ? 'opacity-80' : 'opacity-50'}`}>{count}</span>
              </button>
            ))}
            {activeTag && (
              <button
                onClick={() => setActiveTag('')}
                data-testid="insights-tag-clear"
                className="text-[#83BF42] text-xs font-['Inter'] underline ml-2"
              >
                Clear tag
              </button>
            )}
          </div>
        )}

        {(query || activeTag) && (
          <p className="text-[#4A4A48] text-sm font-['Lato'] mt-4" data-testid="insights-search-results-count">
            {filtered.length} {filtered.length === 1 ? 'article' : 'articles'}
            {query ? ` matching "${query}"` : ''}
            {activeTag ? ` tagged #${activeTag}` : ''}
          </p>
        )}
      </div>

      {/* Featured Article (only when no filter / no search) */}
      {showFeatured && articles.length > 0 && (
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="group cursor-pointer"
            data-testid="insights-featured-article"
            onClick={() => navigate(`/insights/${articles[0].slug}`)}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="relative h-[300px] md:h-[400px] rounded-2xl overflow-hidden">
                <img
                  src={articles[0].image}
                  alt={articles[0].title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#83BF42] text-white text-xs font-medium font-['Inter'] px-3 py-1.5 rounded-full">
                    Featured
                  </span>
                </div>
              </div>
              <div className="py-4">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[#83BF42] text-xs uppercase tracking-wider font-bold font-['Inter']">
                    {articles[0].category}
                  </span>
                  <span className="text-[#B8B8B8] text-xs">|</span>
                  <span className="text-[#B8B8B8] text-xs font-['Lato'] flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {articles[0].date}
                  </span>
                  <span className="text-[#B8B8B8] text-xs font-['Lato'] flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {articles[0].readTime}
                  </span>
                </div>
                <h2 className="text-[#242326] text-2xl md:text-4xl font-medium font-['Inter'] leading-tight mb-4 group-hover:text-[#83BF42] transition-colors duration-300">
                  {articles[0].title}
                </h2>
                <p className="text-[#4A4A48] text-base md:text-lg font-['Lato'] font-light leading-relaxed mb-6">
                  {articles[0].excerpt}
                </p>
                <span className="inline-flex items-center gap-2 text-[#83BF42] text-sm font-['Inter'] group-hover:gap-3 transition-all">
                  Read article <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Article Grid */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pb-8">
        {filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="insights-empty-state">
            <p className="text-[#4A4A48] font-['Lato'] text-lg">No articles match your filters.</p>
            <button
              onClick={clearAll}
              className="mt-4 text-[#83BF42] text-sm font-['Inter'] hover:underline"
              data-testid="insights-clear-all"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(showFeatured ? filtered.slice(1) : filtered).map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ duration: 0.5, delay: (index % 6) * 0.08 }}
                className="group cursor-pointer"
                data-testid={`insights-article-card-${article.slug}`}
                onClick={() => navigate(`/insights/${article.slug}`)}
              >
                <div className="relative h-[220px] md:h-[240px] rounded-xl overflow-hidden mb-5">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/90 backdrop-blur-sm text-[#242326] text-xs font-medium font-['Inter'] px-3 py-1 rounded-full">
                      {article.category}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-2">
                  <span className="text-[#B8B8B8] text-xs font-['Lato'] flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {article.date}
                  </span>
                  <span className="text-[#B8B8B8] text-xs font-['Lato'] flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {article.readTime}
                  </span>
                </div>

                <h3 className="text-[#242326] text-lg md:text-xl font-medium font-['Inter'] leading-snug mb-2 group-hover:text-[#83BF42] transition-colors duration-300">
                  {article.title}
                </h3>

                <p className="text-[#4A4A48] text-sm font-['Lato'] font-light leading-relaxed line-clamp-2">
                  {article.excerpt}
                </p>
              </motion.article>
            ))}
          </div>
        )}
      </div>

      {/* Popular + Newsletter archive callout — only when no active filter (clean default state) */}
      {showFeatured && (popular.length > 0 || true) && (
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pb-12 grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="insights-secondary-row">
          {/* Popular widget */}
          {popular.length > 0 && (
            <div className="lg:col-span-2 rounded-2xl border border-[#242326]/8 p-6 md:p-8 bg-white" data-testid="insights-popular">
              <div className="flex items-center gap-2 mb-5">
                <TrendingUp className="w-4 h-4 text-[#83BF42]" />
                <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-bold font-['Inter']">
                  Most read
                </p>
              </div>
              <ol className="space-y-3.5">
                {popular.slice(0, 4).map((p, i) => (
                  <li key={p.slug}>
                    <button
                      onClick={() => navigate(`/insights/${p.slug}`)}
                      data-testid={`insights-popular-${p.slug}`}
                      className="text-left w-full flex items-start gap-4 group"
                    >
                      <span className="text-[#B8B8B8] text-2xl font-light font-['Inter'] tabular-nums w-7 shrink-0 group-hover:text-[#83BF42] transition-colors">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div className="min-w-0">
                        <p className="text-[#242326] text-base md:text-lg font-medium font-['Inter'] leading-snug group-hover:text-[#83BF42] transition-colors">
                          {p.title}
                        </p>
                        <p className="text-[#B8B8B8] text-xs font-['Lato'] mt-1 flex items-center gap-2">
                          <span>{p.category}</span>
                          {p.views > 0 && <span>· {p.views} reads</span>}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Newsletter archive callout */}
          <button
            onClick={() => navigate('/newsletter')}
            data-testid="insights-newsletter-archive-cta"
            className="text-left rounded-2xl bg-[#242326] p-6 md:p-8 hover:bg-[#1a1a1d] transition-colors group flex flex-col justify-between min-h-[180px]"
          >
            <div>
              <Mail className="w-6 h-6 text-[#83BF42] mb-3" />
              <h4 className="text-white text-xl md:text-2xl font-light font-['Inter'] leading-tight">
                Newsletter archive
              </h4>
              <p className="text-white/50 text-sm font-['Lato'] font-light mt-2">
                Catch up on every issue we&rsquo;ve sent.
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[#83BF42] text-sm font-['Inter'] mt-5 group-hover:gap-2.5 transition-all">
              Browse issues <ArrowRight className="w-4 h-4" />
            </span>
          </button>
        </div>
      )}

      {/* Newsletter signup */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pb-20">
        <NewsletterSignup />
      </div>
    </div>
  );
};

export default InsightsPage;
